import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Bold,
  Heading2,
  Heading3,
  Image,
  Italic,
  Link,
  List,
  ListOrdered,
  AlignCenter,
  AlignLeft,
  Pilcrow,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { normalizeUrl } from "@/lib/urlUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const absoluteAssetUrl = (url) => {
  if (!url) return "";
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  const baseUrl = BACKEND_URL || window.location.origin;
  return new URL(url, baseUrl).toString();
};

export const RichTextEditor = ({ value, onChange, testId, imageUploadEndpoint = "", videoUploadEndpoint = "", token = "" }) => {
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const lastHtmlRef = useRef(value || "");
  const selectionRef = useRef(null);

  useEffect(() => {
    const editor = editorRef.current;
    const nextValue = value || "";
    if (!editor || nextValue === editor.innerHTML) return;
    editor.innerHTML = nextValue;
    lastHtmlRef.current = nextValue;
  }, [value]);

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection?.rangeCount) {
      selectionRef.current = selection.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    if (!selection || !selectionRef.current) return;
    selection.removeAllRanges();
    selection.addRange(selectionRef.current);
  };

  const syncValue = () => {
    const nextHtml = editorRef.current?.innerHTML || "";
    lastHtmlRef.current = nextHtml;
    onChange(nextHtml);
  };

  const runCommand = (command, commandValue = null) => {
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand(command, false, commandValue);
    syncValue();
    saveSelection();
  };

  const insertHtml = (html) => {
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand("insertHTML", false, html);
    syncValue();
    saveSelection();
  };

  const insertLink = () => {
    const url = window.prompt("Paste the link URL");
    if (!url) return;
    const normalizedUrl = normalizeUrl(url);
    const selectedText = window.getSelection()?.toString()?.trim();
    const label = window.prompt("Link text", selectedText || "Visit link") || selectedText || "Visit link";
    insertHtml(`<a href="${normalizedUrl}" target="_blank" rel="noopener noreferrer">${label}</a>`);
  };

  const insertImage = () => {
    if (imageUploadEndpoint) {
      imageInputRef.current?.click();
      return;
    }
    const url = window.prompt("Paste the image or GIF URL");
    if (!url) return;
    runCommand("insertImage", normalizeUrl(url));
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${BACKEND_URL}${imageUploadEndpoint}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const imageUrl = response.data.image_url || response.data.logo_url;
      if (!imageUrl) {
        toast.error("Image uploaded, but no image URL was returned");
        return;
      }
      runCommand("insertImage", absoluteAssetUrl(imageUrl));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    }
  };

  const insertVideoLink = () => {
    if (videoUploadEndpoint) {
      videoInputRef.current?.click();
      return;
    }
    const url = window.prompt("Paste the video URL");
    if (!url) return;
    const normalizedUrl = normalizeUrl(url);
    const label = window.prompt("Video link text", "Watch the video") || "Watch the video";
    insertHtml(`<a href="${normalizedUrl}" target="_blank" rel="noopener noreferrer">${label}</a>`);
  };

  const uploadVideo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${BACKEND_URL}${videoUploadEndpoint}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const videoUrl = response.data.video_url;
      if (!videoUrl) {
        toast.error("Video uploaded, but no video URL was returned");
        return;
      }
      const label = window.prompt("Video link text", "Watch the video") || "Watch the video";
      const href = absoluteAssetUrl(videoUrl);
      insertHtml(`<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload video");
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap gap-1 border-b border-slate-100 p-2">
        <Button type="button" variant="ghost" size="sm" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("bold")} title="Bold">
          <Bold className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("italic")} title="Italic">
          <Italic className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("formatBlock", "p")} title="Paragraph">
          <Pilcrow className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("formatBlock", "h2")} title="Heading">
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("formatBlock", "h3")} title="Small heading">
          <Heading3 className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("insertUnorderedList")} title="Bulleted list">
          <List className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("insertOrderedList")} title="Numbered list">
          <ListOrdered className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("justifyLeft")} title="Align left">
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand("justifyCenter")} title="Center text">
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={(event) => event.preventDefault()} onClick={insertLink} title="Add link">
          <Link className="h-4 w-4" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={(event) => event.preventDefault()} onClick={insertImage} title="Add image or GIF">
          <Image className="h-4 w-4" />
        </Button>
        {imageUploadEndpoint && (
          <input
            ref={imageInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            onChange={uploadImage}
            className="hidden"
          />
        )}
        {videoUploadEndpoint && (
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm"
            onChange={uploadVideo}
            className="hidden"
          />
        )}
        <Button type="button" variant="ghost" size="sm" onMouseDown={(event) => event.preventDefault()} onClick={insertVideoLink} title={videoUploadEndpoint ? "Upload video" : "Add video link"}>
          <Video className="h-4 w-4" />
        </Button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="prose prose-sm min-h-[220px] max-w-none overflow-auto p-4 text-sm text-slate-700 focus:outline-none [&_a]:font-bold [&_a]:text-blue-600 [&_a]:underline [&_a]:underline-offset-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:my-1"
        onBlur={saveSelection}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onInput={(event) => {
          lastHtmlRef.current = event.currentTarget.innerHTML;
          onChange(event.currentTarget.innerHTML);
          saveSelection();
        }}
        data-testid={testId}
      />
    </div>
  );
};
